import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ErrorService } from '../../../../@core/utils/error.service';
import { ConfirmationDialogComponent } from '../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { lotInventory, lotInventoryService } from '../../laboratory/services/lab-quantity-service';
import { InventoryService } from '../../pick-plan/services/inventory.service';
import { HandlingUnitMasterDTO } from '../models/handling-unit-master';
import { InventoryLotDTO } from '../models/inventory-lot';
import { InventoryLotEditComponent } from '../pages/inventory-lots/inventory-lot-edit/inventory-lot-edit.component';
import { LotEditProductPopupComponent } from '../pages/inventory-lots/lot-edit-product-popup/lot-edit-product-popup.component';
import { LotDetailsByRoomDialogComponent } from '../widgets/lot-details-by-room/lot-details-by-room-dialog/lot-details-by-room-dialog.component';
import { LotDetailsByRoomMoveInventoryComponent } from '../widgets/lot-details-by-room/lot-details-by-room-move-inventory/lot-details-by-room-move-inventory.component';

/**
 * Admin lot actions shared by the Lots list and the Lot Dashboard.
 * Each emits once, `true` when the lot changed and the caller should reload.
 */
@Injectable({ providedIn: 'root' })
export class LotActionsService {

  constructor(
    private dialog: MatDialog,
    private inventoryService: InventoryService,
    private lotInventoryService: lotInventoryService,
    private errorService: ErrorService
  ) { }

  /** Pass `{}` to create a lot. Always reloads after close, as the edit dialog doesn't report a result. */
  editLot(lot: InventoryLotDTO): Observable<boolean> {
    return this.dialog.open(InventoryLotEditComponent, { width: '900px', maxWidth: '95vw', data: { lot, isPopup: true }, disableClose: false })
      .afterClosed().pipe(map(() => true));
  }

  editProduct(lot: InventoryLotDTO): Observable<boolean> {
    return LotEditProductPopupComponent.open(this.dialog, { lot })
      .afterClosed().pipe(map(saved => !!saved));
  }

  moveInventory(lot: InventoryLotDTO): Observable<boolean> {
    return this.lotHums(lot).pipe(
      switchMap(hums => {
        const lotHuds = hums.flatMap(hum => hum.handlingUnitDetails ?? []).filter(hud => hud.lotId == lot.id);

        // The move widget reads the selected HUD/HUM ids off the lot it's handed.
        const moveData = {
          lot: {
            ...lot,
            handlingUnitDetailId: lotHuds.map(hud => hud.id),
            handlingUnitMasterId: lotHuds.map(hud => hud.handlingUnitMasterId),
          },
          isPopup: true,
          type: 'transfer',
          lotId: lot.id,
          lotName: lot.lotNumber,
        };

        return LotDetailsByRoomMoveInventoryComponent.open(this.dialog, moveData).afterClosed();
      }),
      map(job => job == 'reload')
    );
  }

  adjustQuantity(lot: InventoryLotDTO): Observable<boolean> {
    return this.lotHums(lot).pipe(
      switchMap(hums => {
        const huds = hums.flatMap(hum => hum.handlingUnitDetails ?? []);
        if (!huds.length) {
          this.dialog.open(ConfirmationDialogComponent, {
            minWidth: '10%',
            data: {
              confirmLabel: 'Ok',
              title: 'No Active Inventory',
              confirmMessage: 'There is no active inventory for this lot.',
              isWarning: true
            }
          });
          return EMPTY;
        }

        const lotNumber = huds[0].lotNumber;
        const lotHuds = huds.filter(hud => hud.lotNumber === lotNumber);
        const quantity = lotHuds.reduce((sum, hud) => sum + hud.quantity, 0);
        const adjustData = {
          lotNumber,
          lotId: lotHuds[0].lotId,
          receiptDate: new Date(lotHuds[0].createdDate),
          handlingUnitDetailId: lotHuds.map(hud => hud.id),
          handlingUnitMasterId: lotHuds.map(hud => hud.handlingUnitMasterId),
          quantity,
          originalQuantity: quantity
        };

        return LotDetailsByRoomDialogComponent.open(this.dialog, adjustData).afterClosed();
      }),
      switchMap((val: lotInventory) => {
        if (!val)
          return of(false);

        const adjustment = val.quantity - val.originalQuantity;
        val.quantity = Math.abs(adjustment);
        const request = adjustment > 0
          ? this.lotInventoryService.lotInventoryAdd(val)
          : this.lotInventoryService.lotInventoryRemove(val);

        return request.pipe(map(() => true), this.handleError());
      })
    );
  }

  private lotHums(lot: InventoryLotDTO): Observable<HandlingUnitMasterDTO[]> {
    return this.inventoryService.getInventoryHUMs(false, lot.id).pipe(
      map(hums => hums ?? []),
      this.handleError()
    );
  }

  private handleError<T>() {
    return catchError<T, Observable<never>>(err => {
      this.errorService.handleError(err);
      return EMPTY;
    });
  }
}
